import React, { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Edit, Trash2, Users, Mail, Phone, MapPin } from "lucide-react";

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
  });

  const fetchClients = async () => {
    try {
      const response = await API.get("/clients");
      setClients(response.data);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editClient) {
        await API.put(`/clients/${editClient.client_id}`, form);
        toast.success("Client updated");
      } else {
        await API.post("/clients", form);
        toast.success("Client created");
      }
      setDialogOpen(false);
      setEditClient(null);
      setForm({ name: "", email: "", phone: "", address: "", notes: "" });
      fetchClients();
    } catch (error) {
      toast.error("Failed to save client");
    }
  };

  const handleEdit = (client) => {
    setEditClient(client);
    setForm({
      name: client.name,
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      notes: client.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await API.delete(`/clients/${clientId}`);
      toast.success("Client deleted");
      fetchClients();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading clients...</div>;
  }

  return (
    <div data-testid="clients-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Clients</h1>
          <p className="text-slate-400 font-ibm mt-1">Manage your client contacts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-client-btn"
              onClick={() => {
                setEditClient(null);
                setForm({ name: "", email: "", phone: "", address: "", notes: "" });
              }}
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold"
            >
              <Plus size={18} className="mr-2" />
              New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="font-chivo text-white">{editClient ? "Edit Client" : "New Client"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-400">Client Name *</label>
                <Input
                  data-testid="client-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g., John Smith"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Email</label>
                <Input
                  type="email"
                  data-testid="client-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="client@email.com"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Phone</label>
                <Input
                  data-testid="client-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Address</label>
                <Input
                  data-testid="client-address-input"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="123 Main St, City, State"
                  className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-400">Notes</label>
                <textarea
                  data-testid="client-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-md text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  rows={3}
                  placeholder="Client notes..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-client-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
                  {editClient ? "Update" : "Create"} Client
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Clients Grid */}
      {clients.length === 0 ? (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
          <Users size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No clients yet</h3>
          <p className="text-slate-500 mb-4">Add your first client to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((client) => (
            <div
              key={client.client_id}
              data-testid={`client-card-${client.client_id}`}
              className="bg-slate-900 border border-slate-800 rounded-lg p-6 hover:border-slate-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
                    <span className="text-lg font-semibold text-slate-300">
                      {client.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-chivo font-semibold text-lg text-white">{client.name}</h3>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {client.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Mail size={14} className="text-slate-500" />
                    <a href={`mailto:${client.email}`} className="hover:text-emerald-400">
                      {client.email}
                    </a>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Phone size={14} className="text-slate-500" />
                    <a href={`tel:${client.phone}`} className="hover:text-emerald-400">
                      {client.phone}
                    </a>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <MapPin size={14} className="text-slate-500" />
                    <span>{client.address}</span>
                  </div>
                )}
              </div>

              {client.notes && (
                <p className="text-sm text-slate-500 mb-4 line-clamp-2">{client.notes}</p>
              )}

              <div className="flex items-center gap-2 pt-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`edit-client-${client.client_id}`}
                  onClick={() => handleEdit(client)}
                  className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  <Edit size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid={`delete-client-${client.client_id}`}
                  onClick={() => handleDelete(client.client_id)}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
